import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Skeleton, Link, Chip, IconButton, Tooltip } from '@mui/material';
import { getGameScores } from '../services/api';
import { getSteamReviews } from '../services/SteamApi';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Tips from './dialogs/Tips';

interface ScoresSectionProps {
  gameId: number;
  gameName: string | null;
  gameJapaneseName: string | null;
  pulseRating: number | null;
  pulseRatingCount: number | null;
  steamAppId?: string | null;
}

interface MetacriticData {
  slug: string | null;
  platform: string | null;
  metascore: number | null;
  metascore_count: number | null;
  user_score: number | null;
  user_score_count: number | null;
}

interface HLTBData {
  hltb_id: number | null;
  review_score: number | null;
  review_count: number | null;
}

interface SteamReviewData {
  total_reviews: number;
  total_positive: number;
  review_score: number;
  review_score_desc: string;
}

interface ScoresData {
  metacritic: MetacriticData | null;
  hltb: HLTBData | null;
}

const ScoresSection: React.FC<ScoresSectionProps> = ({ gameId, gameName, gameJapaneseName, pulseRating, pulseRatingCount, steamAppId }) => {
  const [scoresData, setScoresData] = useState<ScoresData | null>(null);
  const [steamReviews, setSteamReviews] = useState<{ global: SteamReviewData | null; japan: SteamReviewData | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  // IntersectionObserverで要素が画面内に入ったかを検知
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 } // 10%が見えたら発火
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isVisible]);

  // 画面内に入ったらデータを取得
  useEffect(() => {
    if (!isVisible) return;

    const fetchScoresData = async () => {
      try {
        const data = await getGameScores(gameId);
        setScoresData(data);
      } catch (error) {
        console.error('スコアデータの取得に失敗しました:', error);
        // エラーでも null をセット（Steam データは別途取得）
        setScoresData({ metacritic: null, hltb: null });
      }

      // Steam App IDがある場合はSteamレビューも取得（独立して実行）
      if (steamAppId) {
        try {
          const steamData = await getSteamReviews(steamAppId);
          setSteamReviews(steamData);
        } catch (error) {
          console.error('Steamレビューの取得に失敗しました:', error);
        }
      }

      setLoading(false);
    };

    fetchScoresData();
  }, [isVisible, gameId, steamAppId]);

  const getScoreColor = (score: number | null, maxScore: number = 100): string => {
    if (score === null) return 'rgba(255, 255, 255, 0.7)';
    const normalizedScore = maxScore === 10 ? score * 10 : score;
    if (normalizedScore >= 80) return '#66cc33';
    if (normalizedScore >= 50) return '#ffcc33';
    return '#ff6666';
  };

  const getSteamColor = (reviewScore: number): string => {
    if (reviewScore >= 6) return '#66BB6A'; // 好評 - Green
    if (reviewScore >= 4) return '#FFA726'; // 賛否両論 - Orange
    return '#EF5350'; // 不評 - Red
  };

  const getSteamDescription = (reviewScoreDesc: string): string => {
    const descMap: { [key: string]: string } = {
      "Overwhelmingly Positive": "圧倒的に好評",
      "Very Positive": "非常に好評",
      "Positive": "好評",
      "Mostly Positive": "やや好評",
      "Mixed": "賛否両論",
      "Mostly Negative": "やや不評",
      "Negative": "不評",
      "Very Negative": "非常に不評",
      "Overwhelmingly Negative": "圧倒的に不評",
    };
    return descMap[reviewScoreDesc] || reviewScoreDesc;
  };

  const formatCount = (count: number | null): string => {
    if (count === null || count === undefined) return '-';
    if (count <= 10) return '<10';

    if (count >= 1000) {
      // 1000以上：下2桁を切り捨て（1135→1,100+）
      const rounded = Math.floor(count / 100) * 100;
      return `${rounded.toLocaleString()}+`;
    } else {
      // 1000未満：下1桁を切り捨て（986→980+）
      const rounded = Math.floor(count / 10) * 10;
      return `${rounded}+`;
    }
  };

  const formatScore = (score: number | null, isDecimal: boolean = false, multiply10: boolean = false): string => {
    if (score === null) return '-';
    if (isDecimal) {
      const numScore = typeof score === 'number' ? score : Number(score);
      return numScore.toFixed(1);
    }
    if (multiply10) {
      const numScore = typeof score === 'number' ? score : Number(score);
      return (numScore * 10).toString();
    }
    return score.toString();
  };

  const metacritic = scoresData?.metacritic;
  const hltb = scoresData?.hltb;

  // PULSEだけの場合、または全てのスコアがnullの場合は何も表示しない
  const hasMetacriticScore = metacritic !== null;
  const hasHltbScore = hltb !== null;
  const hasSteamScore = (steamReviews?.global && steamReviews.global.total_reviews > 0) ||
                        (steamReviews?.japan && steamReviews.japan.total_reviews > 0);

  const hasAnyNonPulseScore = hasMetacriticScore || hasHltbScore || hasSteamScore;

  // ローディング中またはスコアがない場合でも、要素を表示して IntersectionObserver が動作できるようにする
  if (loading) {
    return (
      <Box ref={sectionRef} sx={{ mt: { xs: 3, sm: 3 } }}>
        <Typography variant="h2" gutterBottom sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>スコア</Typography>
        <Grid container spacing={1}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={4} key={i}>
              <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // データ読み込み完了後、スコアがなければ非表示
  if (!hasAnyNonPulseScore) {
    return null;
  }

  const metacriticUrl = metacritic?.slug
    ? `https://www.metacritic.com/game/${metacritic.slug}`
    : null;
  const hltbUrl = hltb?.hltb_id
    ? `https://howlongtobeat.com/game/${hltb.hltb_id}`
    : null;

  // 全レビュー数を計算（Steamはglobalのみ、japanはglobalに含まれるので除外）
  const totalReviewCount =
    (pulseRatingCount || 0) +
    (metacritic?.metascore_count || 0) +
    (metacritic?.user_score_count || 0) +
    (hltb?.review_count || 0) +
    (steamReviews?.global?.total_reviews || 0);

  // PULSEスコアを10-100スケールに変換（再利用）
  const pulseScoreConverted = pulseRating !== null ? (pulseRating - 20) * 1.125 + 10 : null;

  // Steamスコアと他のスコアの乖離をチェック（評価数10以上のみ）
  // 返り値: { hasWarning: boolean, isHigher: boolean }
  const checkSteamDivergence = (steamScore: number): { hasWarning: boolean; isHigher: boolean } => {
    const scores: number[] = [];

    // PULSEスコア（評価数10超）
    if (pulseScoreConverted !== null && (pulseRatingCount ?? 0) > 10) {
      scores.push(pulseScoreConverted);
    }

    // Metascore（評価数10超）
    if (metacritic && metacritic.metascore !== null && (metacritic.metascore_count ?? 0) > 10) {
      scores.push(metacritic.metascore);
    }

    // HLTB（評価数10超）
    if (hltb && hltb.review_score !== null && (hltb.review_count ?? 0) > 10) {
      scores.push(hltb.review_score);
    }

    // 比較対象が1つ以上ある場合のみチェック
    if (scores.length === 0) return { hasWarning: false, isHigher: false };

    const average = scores.reduce((a, b) => a + b, 0) / scores.length;
    const difference = steamScore - average;
    const divergence = Math.abs(difference);

    // 20ポイント以上の乖離で警告、ただし条件付き
    if (divergence < 20) {
      return { hasWarning: false, isHigher: false };
    }

    // 下方乖離の場合：他のスコア平均が75点以上の場合のみ警告（PC版固有の問題の可能性）
    if (difference < 0 && average < 75) {
      return { hasWarning: false, isHigher: false };
    }

    // 上方乖離の場合：Steamスコアが70点以下なら警告不要（そもそも低評価）
    if (difference > 0 && steamScore <= 70) {
      return { hasWarning: false, isHigher: false };
    }

    return {
      hasWarning: true,
      isHigher: difference > 0  // Steamが他より高い場合true
    };
  };

  return (
    <Box ref={sectionRef} sx={{ mt: { xs: 2, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography variant="h2" gutterBottom sx={{ mb: 0, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>評価スコア</Typography>
        {totalReviewCount > 0 && (
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '0.7rem',
              padding: '2px 8px',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {formatCount(totalReviewCount)}
          </Box>
        )}
        <Tips
          title="スコアについて"
          content={
            <Box>
              <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
                <strong>PULSE</strong>: PULSEコミュニティによる評価
              </Typography>
              <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
                <strong>Metascore</strong>: プロの批評家レビューの加重平均スコア
              </Typography>
              <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
                <strong>User Score</strong>: Metacriticの一般ユーザー評価
              </Typography>
              <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
                <strong>HLTB</strong>: HowLongToBeatコミュニティによる評価
              </Typography>
              <Typography variant="body2" paragraph sx={{ mb: 0.5 }}>
                <strong>Steam</strong>: 推奨した購入者の割合
              </Typography>
              <Typography variant="body2" paragraph sx={{ mb: 1.5, pl: 2, fontSize: '0.85em', color: 'text.secondary' }}>
                GL: 世界 / JP: 日本
              </Typography>
              <Typography variant="caption" sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.1)', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
                Data from{' '}
                {metacriticUrl && (
                  <Link href={metacriticUrl} target="_blank" rel="noopener noreferrer" sx={{ color: 'text.secondary' }}>
                    Metacritic
                  </Link>
                )}
                {metacriticUrl && hltbUrl && ' / '}
                {hltbUrl && (
                  <Link href={hltbUrl} target="_blank" rel="noopener noreferrer" sx={{ color: 'text.secondary' }}>
                    HowLongToBeat
                  </Link>
                )}
              </Typography>
            </Box>
          }
        />
      </Box>
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={3}>
          <Box sx={{
            p: { xs: 1, sm: 1.5 },
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1 }}>
                PULSE
              </Typography>
              <Chip
                label="JP"
                size="small"
                sx={{
                  height: '14px',
                  fontSize: '0.6rem',
                  fontWeight: 400,
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '3px',
                  display: { xs: 'none', sm: 'inline-flex' },
                  '& .MuiChip-label': { px: 0.75, py: 0 }
                }}
              />
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: '600',
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              color: pulseScoreConverted === null ? 'rgba(255, 255, 255, 0.4)' : ((pulseRatingCount ?? 0) <= 10 ? 'rgba(255, 255, 255, 0.4)' : getScoreColor(pulseScoreConverted))
            }}>
              {pulseScoreConverted === null ? '-' : Math.round(pulseScoreConverted)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonOutlineIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: 'pulse.info' }} />
              <Typography variant="caption" color="pulse.info" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                {formatCount(pulseRatingCount)}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={3}>
          <Box sx={{
            p: { xs: 1, sm: 1.5 },
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1 }}>
                MetaScore
              </Typography>
              <Chip
                label="GL"
                size="small"
                variant="outlined"
                sx={{
                  height: '14px',
                  fontSize: '0.6rem',
                  fontWeight: 400,
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.5)',
                  borderRadius: '3px',
                  display: { xs: 'none', sm: 'inline-flex' },
                  '& .MuiChip-label': { px: 0.75, py: 0 }
                }}
              />
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: '600',
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              color: metacritic?.metascore === null ? 'rgba(255, 255, 255, 0.4)' : ((metacritic?.metascore_count ?? 0) <= 10 ? 'rgba(255, 255, 255, 0.4)' : getScoreColor(metacritic?.metascore ?? null))
            }}>
              {formatScore(metacritic?.metascore ?? null)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonOutlineIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: 'pulse.info' }} />
              <Typography variant="caption" color="pulse.info" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                {formatCount(metacritic?.metascore_count ?? null)}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={3}>
          <Box sx={{
            p: { xs: 1, sm: 1.5 },
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1 }}>
                UserScore
              </Typography>
              <Chip
                label="GL"
                size="small"
                variant="outlined"
                sx={{
                  height: '14px',
                  fontSize: '0.6rem',
                  fontWeight: 400,
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.5)',
                  borderRadius: '3px',
                  display: { xs: 'none', sm: 'inline-flex' },
                  '& .MuiChip-label': { px: 0.75, py: 0 }
                }}
              />
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: '600',
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              color: metacritic?.user_score === null ? 'rgba(255, 255, 255, 0.4)' : ((metacritic?.user_score_count ?? 0) <= 10 ? 'rgba(255, 255, 255, 0.4)' : getScoreColor(metacritic?.user_score ?? null, 10))
            }}>
              {formatScore(metacritic?.user_score ?? null, false, true)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonOutlineIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: 'pulse.info' }} />
              <Typography variant="caption" color="pulse.info" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                {formatCount(metacritic?.user_score_count ?? null)}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={3}>
          <Box sx={{
            p: { xs: 1, sm: 1.5 },
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1 }}>
                HLTB
              </Typography>
              <Chip
                label="GL"
                size="small"
                variant="outlined"
                sx={{
                  height: '14px',
                  fontSize: '0.6rem',
                  fontWeight: 400,
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.5)',
                  borderRadius: '3px',
                  display: { xs: 'none', sm: 'inline-flex' },
                  '& .MuiChip-label': { px: 0.75, py: 0 }
                }}
              />
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: '600',
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              color: hltb?.review_score === null ? 'rgba(255, 255, 255, 0.4)' : ((hltb?.review_count ?? 0) <= 10 ? 'rgba(255, 255, 255, 0.4)' : getScoreColor(hltb?.review_score ?? null))
            }}>
              {formatScore(hltb?.review_score ?? null)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonOutlineIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: 'pulse.info' }} />
              <Typography variant="caption" color="pulse.info" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                {formatCount(hltb?.review_count ?? null)}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Steam Reviews Section - 2 columns */}
      <Grid container spacing={1}>
        {steamReviews?.global && steamReviews.global.total_reviews > 0 && (() => {
          // Steamのreview_scoreは0-9のスケール、好評度パーセンテージを100点満点として使用
          const steamScore = Math.round((steamReviews.global.total_positive / steamReviews.global.total_reviews) * 100);
          const steamColor = getSteamColor(steamReviews.global.review_score);
          const steamDesc = getSteamDescription(steamReviews.global.review_score_desc);
          const divergence = checkSteamDivergence(steamScore);
          return (
            <Grid item xs={6}>
              <Box sx={{
                p: { xs: 1, sm: 1.5 },
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1 }}>
                    Steam
                  </Typography>
                  <Chip
                    label="GL"
                    size="small"
                    variant="outlined"
                    sx={{
                      height: '14px',
                      fontSize: '0.6rem',
                      fontWeight: 400,
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'rgba(255, 255, 255, 0.5)',
                      borderRadius: '3px',
                      '& .MuiChip-label': { px: 0.75, py: 0 }
                    }}
                  />
                  {divergence.hasWarning && (
                    <Tooltip
                      title={
                        <Box sx={{ p: 0.5 }}>
                          {divergence.isHigher ? (
                            <>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', mb: 0.5, fontWeight: 600 }}>
                                留意点
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                                他のスコアと大きく乖離しています。二択式のため「おすすめしないほどではない」も推奨になります。一般的なレビューにおける高評価とは意味合いが異なります。
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', mb: 0.5, fontWeight: 600 }}>
                                PC版固有の問題の可能性
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                                他のスコアと大きく乖離しています。PC（Steam）特有の不具合やパフォーマンス問題がある可能性があります。
                              </Typography>
                            </>
                          )}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <WarningAmberIcon sx={{
                        fontSize: '0.9rem',
                        color: divergence.isHigher ? '#66BB6A' : '#FFA726',
                        cursor: 'help'
                      }} />
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography variant="h6" sx={{
                      fontWeight: '600',
                      fontSize: { xs: '0.8rem', sm: '0.85rem' },
                      color: steamReviews.global.total_reviews <= 10 ? 'rgba(255, 255, 255, 0.4)' : steamColor
                    }}>
                      {steamScore}%
                    </Typography>
                    <Typography variant="caption" sx={{
                      fontSize: { xs: '0.55rem', sm: '0.6rem' },
                      color: 'text.secondary',
                      ml: 0.25
                    }}>
                      が評価
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{
                    fontSize: { xs: '0.55rem', sm: '0.6rem' },
                    color: steamColor,
                    display: { xs: 'none', sm: 'inline' },
                    ml: 0.5
                  }}>
                    - {steamDesc}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonOutlineIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: 'pulse.info' }} />
                  <Typography variant="caption" color="pulse.info" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                    {formatCount(steamReviews.global.total_reviews)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          );
        })()}

        {steamReviews?.japan && steamReviews.japan.total_reviews > 0 && (() => {
          // 日本語レビューも同様に好評度パーセンテージを使用
          const steamJapanScore = Math.round((steamReviews.japan.total_positive / steamReviews.japan.total_reviews) * 100);
          const steamJapanColor = getSteamColor(steamReviews.japan.review_score);
          const steamJapanDesc = getSteamDescription(steamReviews.japan.review_score_desc);
          return (
            <Grid item xs={6}>
              <Box sx={{
                p: { xs: 1, sm: 1.5 },
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1 }}>
                    Steam
                  </Typography>
                  <Chip
                    label="JP"
                    size="small"
                    sx={{
                      height: '14px',
                      fontSize: '0.6rem',
                      fontWeight: 400,
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                      color: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '3px',
                      '& .MuiChip-label': { px: 0.75, py: 0 }
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography variant="h6" sx={{
                      fontWeight: '600',
                      fontSize: { xs: '0.8rem', sm: '0.85rem' },
                      color: steamReviews.japan.total_reviews <= 10 ? 'rgba(255, 255, 255, 0.4)' : steamJapanColor
                    }}>
                      {steamJapanScore}%
                    </Typography>
                    <Typography variant="caption" sx={{
                      fontSize: { xs: '0.55rem', sm: '0.6rem' },
                      color: 'text.secondary',
                      ml: 0.25
                    }}>
                      が評価
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{
                    fontSize: { xs: '0.55rem', sm: '0.6rem' },
                    color: steamJapanColor,
                    display: { xs: 'none', sm: 'inline' },
                    ml: 0.5
                  }}>
                    - {steamJapanDesc}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonOutlineIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: 'pulse.info' }} />
                  <Typography variant="caption" color="pulse.info" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                    {formatCount(steamReviews.japan.total_reviews)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          );
        })()}
      </Grid>
    </Box>
  );
};

export default ScoresSection;
